import { createContext, useContext } from "react";
import { deburr, get, isNil, uniq, uniqBy } from "lodash";
import {
  isNeedsPath,
  looksLikeNeed,
  needsCategoryOf,
  needsPathOfAccessor,
} from "./needs-fields";
import { BINDING_KINDS, SLOT_TYPES } from "./workshop-view";

const NEEDS_LABEL_PREFIX = /^\s*besoin\s*·\s*/i;

const CUSTOM_PREFIX = "custom.";

const PLANS_COLLECTION = "plans";

const CHOICE_KINDS = ["radioGroup", "radio", "select", "slider"];

const CATEGORY_TERMS = {
  hospitalization: ["hospitalisation", "hopital", "hospitalier"],
  medicalExpenses: [
    "medecin",
    "soins courants",
    "generaliste",
    "specialiste",
    "consultation",
  ],
  dental: ["dentaire", "dentiste"],
  optical: ["optique", "lunettes", "lentilles"],
  hearingAid: ["aides auditives", "aide auditive", "auditi"],
};

const RECOMMENDED_BADGE =
  /data-visible-when\s*=\s*["']\s*(plans\.)?recommended\s*["']/;

const RECOMMEND_TEXT = /recommand/i;

const SAVING_BINDING_KINDS = [
  BINDING_KINDS.ENTITY_FIELD,
  BINDING_KINDS.SET_FIELD,
];

export const BADGE_STATES = {
  SHOWN: "SHOWN",
  ALWAYS: "ALWAYS",
  MISSING: "MISSING",
  NO_OFFERS: "NO_OFFERS",
};

export const CHECKLIST_ROWS = {
  CATEGORY: "CATEGORY",
  BADGE: "BADGE",
  VALUES: "VALUES",
};

const compact = (text) =>
  deburr(String(isNil(text) ? "" : text))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const noop = () => undefined;

export const RecommendationContext = createContext({
  needsFields: [],
  usage: {},
  stepNames: {},
  focus: null,
  clearFocus: noop,
});

export const useRecommendation = () => useContext(RecommendationContext);

const scaleOf = (options) =>
  (options || [])
    .filter((option) => !isNil(get(option, "value")))
    .map((option) => ({
      value: String(option.value),
      label: String(isNil(option.label) ? option.value : option.label),
    }));

export const needsFieldsOf = (variables) =>
  (variables || [])
    .filter((variable) => isNeedsPath(variable.field))
    .map((variable) => ({
      path: variable.field,
      category: needsCategoryOf(variable.field),
      label: variable.label || variable.field,
      name: String(variable.label || needsCategoryOf(variable.field)).replace(
        NEEDS_LABEL_PREFIX,
        "",
      ),
      scale: scaleOf(variable.options),
    }));

export const scaleText = (field) =>
  get(field, "scale", [])
    .map((option) => `${option.value} ${option.label}`)
    .join(" · ");

const termsOf = (field) =>
  uniq(
    [field.category, field.name, ...(CATEGORY_TERMS[field.category] || [])].map(
      compact,
    ),
  ).filter(Boolean);

const fieldInText = (needsFields, text) => {
  const wanted = compact(text);
  if (!wanted) return null;
  return (
    needsFields.find((field) =>
      termsOf(field).some((term) => wanted.includes(term)),
    ) || null
  );
};

export const slotPathOf = (slot) =>
  SAVING_BINDING_KINDS.includes(get(slot, "binding.kind"))
    ? `${get(slot, "binding.entity")}.${get(slot, "binding.field")}`
    : null;

const customKeyOfPath = (path) =>
  typeof path === "string" && path.startsWith(CUSTOM_PREFIX)
    ? path.slice(CUSTOM_PREFIX.length)
    : null;

export const isChoiceSlot = (slot) =>
  get(slot, "type") === SLOT_TYPES.INPUT &&
  (CHOICE_KINDS.includes(get(slot, "inputKind")) ||
    get(slot, "radioOptions.length", 0) > 0);

const optionsText = (slot) =>
  (get(slot, "radioOptions") || []).map((option) => option.label).join(" ");

export const looksLikeNeedPath = (path) => {
  const key = customKeyOfPath(path);
  return !isNil(key) && looksLikeNeed(key);
};

export const suggestedNeedsField = ({ needsFields, slot, path, stepName }) => {
  const free = isNil(path) || !isNil(customKeyOfPath(path));
  if (!needsFields.length || !isChoiceSlot(slot) || !free) return null;
  for (const text of [
    customKeyOfPath(path),
    get(slot, "label"),
    stepName,
    optionsText(slot),
  ]) {
    const found = fieldInText(needsFields, text);
    if (!isNil(found)) return found;
  }
  return null;
};

export const answerValuesOf = (slot) =>
  uniq(
    (get(slot, "radioOptions") || [])
      .map((option) => String(isNil(option.value) ? "" : option.value))
      .filter((value) => value !== ""),
  );

export const offScaleValues = (slot, field) => {
  const scale = get(field, "scale", []).map((option) => option.value);
  if (!scale.length) return [];
  const values = answerValuesOf(slot);
  return values.some((value) => !scale.includes(value)) ? values : [];
};

const targetsOf = (node) => [
  {
    stateId: null,
    html: get(node, "data.screenHtml") || "",
    slots: get(node, "data.screenSlots") || [],
  },
  ...(get(node, "data.screenStates") || []).map((state) => ({
    stateId: state.id,
    html: get(state, "htmlContent") || "",
    slots: get(state, "bindingManifest.slots") || [],
  })),
];

export const slotsOfStep = (node) =>
  uniqBy(
    targetsOf(node).flatMap((target) => target.slots),
    (slot) => `${slot.id}:${slotPathOf(slot) || ""}`,
  );

export const needsPathsOfStep = (node) =>
  uniq([
    ...slotsOfStep(node).map(slotPathOf),
    ...(get(node, "data.formDefinition.fields") || []).map((field) =>
      needsPathOfAccessor(get(field, "accessor")),
    ),
  ]).filter(isNeedsPath);

export const showsRecommendedBadge = (node) =>
  targetsOf(node).some((target) => RECOMMENDED_BADGE.test(target.html));

const stepOf = (node) => ({ id: node.id, name: get(node, "data.name") || "" });

export const needsUsage = (nodes) =>
  (nodes || []).reduce((usage, node) => {
    for (const path of needsPathsOfStep(node))
      usage[path] = [...(usage[path] || []), stepOf(node)];
    return usage;
  }, {});

const isPlansList = (slot) =>
  get(slot, "type") === SLOT_TYPES.LIST &&
  get(slot, "binding.source") === PLANS_COLLECTION;

const isBadgeText = (slot) =>
  get(slot, "collection") === PLANS_COLLECTION &&
  RECOMMEND_TEXT.test(
    String(get(slot, "binding.sourceValue") || get(slot, "label") || ""),
  );

const offersOf = (nodes) =>
  nodes
    .map((node) => {
      const slots = get(node, "data.screenSlots") || [];
      const list = slots.find(isPlansList);
      return isNil(list)
        ? null
        : { node, list, badge: slots.find(isBadgeText) || null };
    })
    .filter(Boolean);

const badgeRow = (nodes) => {
  const offers = offersOf(nodes);
  if (!offers.length)
    return {
      kind: CHECKLIST_ROWS.BADGE,
      ok: false,
      state: BADGE_STATES.NO_OFFERS,
    };
  const shown = offers.find((offer) => showsRecommendedBadge(offer.node));
  if (!isNil(shown))
    return {
      kind: CHECKLIST_ROWS.BADGE,
      ok: true,
      state: BADGE_STATES.SHOWN,
      step: stepOf(shown.node),
      target: { stepId: shown.node.id, slotId: shown.list.id },
    };
  const fixed = offers.find((offer) => !isNil(offer.badge));
  const offer = fixed || offers[0];
  return {
    kind: CHECKLIST_ROWS.BADGE,
    ok: false,
    state: isNil(fixed) ? BADGE_STATES.MISSING : BADGE_STATES.ALWAYS,
    step: stepOf(offer.node),
    badgeText: get(offer, "badge.binding.sourceValue") || "",
    target: {
      stepId: offer.node.id,
      slotId: get(offer, "badge.id") || offer.list.id,
    },
  };
};

const savingSlotsOf = (nodes, path) =>
  nodes.flatMap((node) =>
    slotsOfStep(node)
      .filter((slot) => slotPathOf(slot) === path)
      .map((slot) => ({ node, slot })),
  );

const candidateOf = (nodes, field, needsFields) =>
  nodes
    .flatMap((node) =>
      slotsOfStep(node).map((slot) => ({
        node,
        slot,
        found: suggestedNeedsField({
          needsFields,
          slot,
          path: slotPathOf(slot),
          stepName: get(node, "data.name"),
        }),
      })),
    )
    .find(({ found }) => get(found, "path") === field.path) || null;

const categoryRow = (nodes, field, needsFields) => {
  const saving = savingSlotsOf(nodes, field.path);
  const steps = uniqBy(
    nodes.filter((node) => needsPathsOfStep(node).includes(field.path)),
    "id",
  ).map(stepOf);
  if (steps.length)
    return {
      kind: CHECKLIST_ROWS.CATEGORY,
      key: field.path,
      ok: true,
      field,
      steps,
      target: {
        stepId: steps[0].id,
        slotId: get(saving, "0.slot.id") || null,
      },
    };
  const candidate = candidateOf(nodes, field, needsFields);
  return {
    kind: CHECKLIST_ROWS.CATEGORY,
    key: field.path,
    ok: false,
    field,
    steps: [],
    candidate: isNil(candidate) ? null : stepOf(candidate.node),
    target: isNil(candidate)
      ? null
      : { stepId: candidate.node.id, slotId: candidate.slot.id },
  };
};

const valuesRow = (nodes, needsFields) => {
  const off = needsFields.flatMap((field) =>
    savingSlotsOf(nodes, field.path)
      .map(({ node, slot }) => ({
        step: stepOf(node),
        slotId: slot.id,
        values: offScaleValues(slot, field),
      }))
      .filter((item) => item.values.length),
  );
  const first = get(off, "0") || null;
  return {
    kind: CHECKLIST_ROWS.VALUES,
    ok: isNil(first),
    scale: scaleText(needsFields[0]),
    off: uniqBy(off, "step.id"),
    target: isNil(first)
      ? null
      : { stepId: first.step.id, slotId: first.slotId },
  };
};

export const recommendationChecklist = ({ nodes, needsFields }) => {
  if (!get(needsFields, "length")) return null;
  const categories = needsFields.map((field) =>
    categoryRow(nodes || [], field, needsFields),
  );
  const asked = categories.some((row) => row.ok);
  return [
    ...categories,
    badgeRow(nodes || []),
    ...(asked ? [valuesRow(nodes || [], needsFields)] : []),
  ];
};
